import { firstNonBlank } from '@/utils/presentation/formatting'

import { PREFERENCE_KEYS } from './constants'
import { IdentifierPreference } from './types'

// Re-export types and constants
export { IdentifierPreference } from './types'

/**
 * Identifier Preference Helpers
 *
 * Architecture Overview:
 * ----------------------
 * This module provides lightweight helpers for reading the user's identifier preference.
 * These helpers read from localStorage for fast, synchronous access.
 *
 *
 * See: @/hooks/use-identifier-preference for the React hook
 *
 * Example usage:
 * ```typescript
 * // In a cell renderer
 * function BondCell({ data }) {
 *   const identifier = selectIdentifier({ cusip: data.cusip, isin: data.isin })
 *   return <div>{identifier}</div>
 * }
 *
 * // In a column definition
 * const columns = [
 *   {
 *     field: 'identifier',
 *     valueGetter: (params) => {
 *       const pref = getIdentifierPreference()
 *       return pref === IdentifierPreference.CUSIP ? params.data.cusip : params.data.isin
 *     }
 *   }
 * ]
 * ```
 */

/**
 * Get the current identifier preference
 * Priority: localStorage > default (CUSIP)
 *
 * Used by components that can't use hooks (like column definitions)
 */
export function getIdentifierPreference(): IdentifierPreference {
  if (typeof window === 'undefined') {
    return IdentifierPreference.CUSIP
  }

  const stored = localStorage.getItem(PREFERENCE_KEYS.IDENTIFIER)
  if (
    stored === IdentifierPreference.CUSIP ||
    stored === IdentifierPreference.ISIN
  ) {
    return stored as IdentifierPreference
  }

  // Default
  return IdentifierPreference.CUSIP
}

/**
 * Set the identifier preference in localStorage
 * Note: This only updates local state. To sync with backend,
 * use the useIdentifierPreference hook's updatePreference function.
 */
export function setIdentifierPreference(preference: IdentifierPreference) {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(PREFERENCE_KEYS.IDENTIFIER, preference)
}

interface InstrumentIdentifiers {
  cusip?: string | null
  isin?: string | null
}

/**
 * The identifier to show plus which of the two it is, so a caller that labels it
 * cannot announce the preference when the fallback rendered. A fund reaches the
 * client with an ISIN and no CUSIP.
 */
export function resolveIdentifier({ cusip, isin }: InstrumentIdentifiers): {
  value: string
  kind: IdentifierPreference
} | null {
  const preferred =
    getIdentifierPreference() === IdentifierPreference.CUSIP
      ? ([
          [IdentifierPreference.CUSIP, cusip],
          [IdentifierPreference.ISIN, isin],
        ] as const)
      : ([
          [IdentifierPreference.ISIN, isin],
          [IdentifierPreference.CUSIP, cusip],
        ] as const)

  for (const [kind, value] of preferred) {
    const present = firstNonBlank(value)
    if (present) return { value: present, kind }
  }
  return null
}

/**
 * Select the identifier (CUSIP or ISIN) based on user preference
 * Used by cell renderers and other components that need the actual identifier value
 *
 * @param cusip - CUSIP identifier
 * @param isin - ISIN identifier
 * @returns The preferred identifier, the other one, or null.
 */
export function selectIdentifier(
  identifiers: InstrumentIdentifiers,
): string | null | undefined {
  return resolveIdentifier(identifiers)?.value ?? null
}
